import { useEffect, useState, useCallback } from 'react';
import Card from '../components/Card';
import Badge from '../components/Badge';
import Button from '../components/Button';
import { typeGroupColor } from '../lib/constants';
import { api } from '../lib/api';
import { useAI } from '../hooks/useAI';
import type { Team, TeamMember, ModeBResponse } from '../lib/types';

export default function TeamAnalysis() {
  const [teams, setTeams] = useState<Team[]>([]);
  const [selectedTeamId, setSelectedTeamId] = useState('');
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [loadingTeams, setLoadingTeams] = useState(true);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const { analyzeTeam, loading: aiLoading, error: aiError } = useAI();
  const [aiResult, setAiResult] = useState<ModeBResponse | null>(null);

  useEffect(() => {
    api.teams.list().then(res => {
      setTeams(res.teams as unknown as Team[]);
    }).catch(() => {}).finally(() => setLoadingTeams(false));
  }, []);

  const loadTeamMembers = useCallback(async (teamId: string) => {
    setLoadingMembers(true);
    setAiResult(null);
    try {
      const { members: m } = await api.teams.get(teamId);
      setMembers(m as unknown as TeamMember[]);
    } catch {
      setMembers([]);
    } finally {
      setLoadingMembers(false);
    }
  }, []);

  useEffect(() => {
    if (selectedTeamId) loadTeamMembers(selectedTeamId);
    else setMembers([]);
  }, [selectedTeamId, loadTeamMembers]);

  const handleAnalyze = async (forceRefresh = false) => {
    if (!selectedTeamId) return;
    const result = await analyzeTeam(selectedTeamId, forceRefresh);
    if (result) setAiResult(result);
  };

  const balanceItems = aiResult?.balance_scores ? [
    { label: 'E / I', score: aiResult.balance_scores.ei },
    { label: 'S / N', score: aiResult.balance_scores.sn },
    { label: 'T / F', score: aiResult.balance_scores.tf },
    { label: 'J / P', score: aiResult.balance_scores.jp },
  ] : [];

  return (
    <div>
      <h2 className="text-[22px] font-bold text-text-main m-0 mb-1">チーム分析</h2>
      <p className="text-[13px] text-text-sub mb-5">チーム構成をAIが分析し、バランス評価と改善提案を行います</p>

      {/* Team Selection */}
      <Card className="mb-5">
        <div className="flex gap-4 items-end flex-wrap mb-4">
          <div className="flex-1 min-w-[200px]">
            <div className="text-[11px] text-text-sub mb-1.5">分析対象チーム</div>
            <select
              value={selectedTeamId}
              onChange={e => setSelectedTeamId(e.target.value)}
              className="w-full px-3 py-2 rounded-lg border border-border bg-surface text-text-main text-[13px] outline-none focus:border-primary"
            >
              <option value="">チームを選択してください</option>
              {teams.map(t => (
                <option key={t.id} value={t.id}>{t.name}（{t.member_count || 0}名）</option>
              ))}
            </select>
          </div>
        </div>

        {loadingTeams && <div className="text-sm text-text-muted">チーム読み込み中...</div>}
        {loadingMembers && <div className="text-sm text-text-muted">メンバー読み込み中...</div>}

        {members.length > 0 && (
          <>
            <div className="text-[13px] font-semibold text-text-main mb-3">
              メンバー（{members.length}名）
            </div>
            <div className="flex gap-2 mb-4 flex-wrap">
              {members.map(m => (
                <div key={m.id} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-surface border border-border">
                  <Badge bg={`${typeGroupColor(m.personality_type)}22`} color={typeGroupColor(m.personality_type)} className="!text-[10px]">
                    {m.personality_type || '?'}
                  </Badge>
                  <span className="text-xs text-text-sub">{m.name}</span>
                </div>
              ))}
            </div>

            {!aiResult && (
              <div>
                <Button primary onClick={() => handleAnalyze()} disabled={aiLoading || members.length < 2}>
                  {aiLoading ? '分析中...' : 'AIでチーム分析を実行 →'}
                </Button>
                {members.length < 2 && (
                  <div className="text-[11px] text-text-muted mt-2">チーム分析には2名以上のメンバーが必要です</div>
                )}
                <div className="text-[11px] text-text-muted mt-2">
                  ※ 氏名等の個人識別情報はAIに送信されません。
                </div>
                {aiError && <div className="text-[11px] text-accent mt-2">{aiError}</div>}
              </div>
            )}
          </>
        )}

        {selectedTeamId && !loadingMembers && members.length === 0 && (
          <div className="text-sm text-text-muted py-4">メンバーが登録されていません</div>
        )}
      </Card>

      {/* Analysis Result */}
      {aiResult && (
        <>
          {/* Re-analyze bar */}
          <div className="flex items-center justify-between mb-4 px-1">
            <div className="flex items-center gap-2">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#4ADE80" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" />
              </svg>
              <span className="text-[13px] text-text-sub">分析完了</span>
            </div>
            <button
              onClick={() => handleAnalyze(true)}
              disabled={aiLoading}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[13px] text-primary border border-primary/30 hover:bg-primary/10 transition-colors disabled:opacity-50"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="23 4 23 10 17 10" /><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
              </svg>
              {aiLoading ? '再分析中...' : 'キャッシュをクリアして再分析'}
            </button>
          </div>

          {/* Balance Scores */}
          <div className="flex gap-3 mb-5 flex-wrap">
            {balanceItems.map((b, idx) => {
              const isGood = b.score.rating === 'バランス良好' || b.score.rating === 'good';
              return (
                <Card key={idx} className="flex-1 min-w-[130px] text-center">
                  <div className="text-[11px] text-text-sub mb-1.5">{b.label}</div>
                  <div className="flex justify-center items-center gap-1">
                    <span className="text-[22px] font-bold text-e-type">
                      {b.score.e_count ?? b.score.s_count ?? b.score.t_count ?? b.score.j_count ?? 0}
                    </span>
                    <span className="text-sm text-text-muted">:</span>
                    <span className="text-[22px] font-bold text-i-type">
                      {b.score.i_count ?? b.score.n_count ?? b.score.f_count ?? b.score.p_count ?? 0}
                    </span>
                  </div>
                  <Badge
                    bg={isGood ? 'rgba(74,222,128,0.1)' : 'rgba(255,107,107,0.1)'}
                    color={isGood ? '#4ADE80' : '#FF6B6B'}
                    className="mt-1.5"
                  >
                    {b.score.rating}
                  </Badge>
                </Card>
              );
            })}
          </div>

          {/* Summary */}
          {aiResult.summary && (
            <Card className="mb-5">
              <div className="text-[13px] font-semibold text-text-main mb-2">総合評価</div>
              <p className="text-[13px] text-text-sub leading-relaxed">{aiResult.summary}</p>
            </Card>
          )}

          {/* Strengths & Reinforcement */}
          <div className="flex gap-4 flex-wrap mb-4">
            <Card className="flex-1 min-w-[260px]">
              <div className="text-[13px] font-semibold text-success mb-2.5">&#x1F4AA; チームの強み</div>
              {aiResult.strengths.map((s, i) => (
                <p key={i} className="text-[13px] text-text-sub leading-relaxed mb-1">{s}</p>
              ))}
            </Card>
            <Card className="flex-1 min-w-[260px]">
              <div className="text-[13px] font-semibold text-warning mb-2.5">&#x1F527; 補強ポイント</div>
              {aiResult.reinforcement_points.map((s, i) => (
                <p key={i} className="text-[13px] text-text-sub leading-relaxed mb-1">{s}</p>
              ))}
            </Card>
          </div>

          {/* Team Tips */}
          {aiResult.team_tips && aiResult.team_tips.length > 0 && (
            <Card>
              <div className="text-[13px] font-semibold text-primary mb-2.5">&#x1F4A1; チーム運営Tips</div>
              {aiResult.team_tips.map((s, i) => (
                <div key={i} className={`text-[13px] text-text-sub py-1.5 ${i < aiResult.team_tips.length - 1 ? 'border-b border-border' : ''}`}>
                  {s}
                </div>
              ))}
            </Card>
          )}

          {/* Reassignment Suggestions */}
          {aiResult.reassignment_suggestions && aiResult.reassignment_suggestions.length > 0 && (
            <Card className="mt-5">
              <div className="text-[13px] font-semibold text-text-main mb-3">&#x1F504; 配置転換レコメンド</div>
              <div className="flex flex-col gap-3">
                {aiResult.reassignment_suggestions.map((s, i) => {
                  const currentColor = s.current_fitness >= 4 ? '#4ADE80' : s.current_fitness >= 3 ? '#FBBF24' : '#FF6B6B';
                  const suggestedColor = s.suggested_fitness >= 4 ? '#4ADE80' : s.suggested_fitness >= 3 ? '#FBBF24' : '#FF6B6B';
                  return (
                    <div key={i} className="p-3 rounded-lg border border-border bg-surface">
                      <div className="flex items-center gap-2 flex-wrap mb-2">
                        <span className="text-[13px] font-semibold text-text-main">{s.member}</span>
                        <span className="text-[12px] text-text-sub">{s.current_role}</span>
                        <Badge
                          bg={`${currentColor}18`}
                          color={currentColor}
                          className="!text-[10px]"
                        >
                          適性 {s.current_fitness}/5
                        </Badge>
                        <span className="text-text-muted text-[16px]">→</span>
                        <span className="text-[12px] text-text-main font-medium">{s.suggested_role}</span>
                        <Badge
                          bg={`${suggestedColor}18`}
                          color={suggestedColor}
                          className="!text-[10px]"
                        >
                          適性 {s.suggested_fitness}/5
                        </Badge>
                      </div>
                      <p className="text-[12px] text-text-sub leading-relaxed mb-1">
                        <span className="text-text-muted font-medium">理由：</span>{s.reason}
                      </p>
                      <p className="text-[12px] text-text-sub leading-relaxed">
                        <span className="text-text-muted font-medium">期待効果：</span>{s.expected_impact}
                      </p>
                    </div>
                  );
                })}
              </div>
              {aiResult.team_optimization_summary && (
                <div className="mt-4 pt-3 border-t border-border">
                  <div className="text-[12px] font-medium text-text-muted mb-1">最適化サマリー</div>
                  <p className="text-[13px] text-text-sub leading-relaxed">{aiResult.team_optimization_summary}</p>
                </div>
              )}
            </Card>
          )}

          {/* Disclaimer */}
          <div className="text-[11px] text-text-muted mt-4 italic">
            {aiResult.disclaimer || '※ 16タイプ分析に基づく参考情報です。'}
          </div>
        </>
      )}
    </div>
  );
}
